import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppConfig } from '@config/AppConfigContext';
import {
  fetchJson,
  getAuthHeader,
  joinUrl,
  toQueryString,
  type QueryParams,
} from './_partner_api_client';

// ── Global defaults ────────────────────────────────────────────────────────────
//
// Import QUERY_DEFAULTS in any hook that cannot use useBasicQuery directly
// (e.g. use_billing_trend_query which uses useQueries for fan-out) so defaults
// stay in a single place.

export const QUERY_DEFAULTS = {
  /** Cache is considered fresh for 5 minutes — avoids redundant API calls during a session. */
  staleTime: 5 * 60 * 1000,
  /** Don't auto-retry on error — partner API errors are usually not transient. */
  retry: false,
} as const;

// ── Types ──────────────────────────────────────────────────────────────────────

/**
 * Serialisable query-string values (undefined/null entries are omitted).
 * Defined in `_partner_api_client` next to the serialiser that consumes it, and
 * re-exported here, which is where callers have always imported it from.
 */
export type { QueryParams };

export interface BasicQueryOptions<TRaw = unknown, TSelected = TRaw> {
  /**
   * Whether the query should fire.
   * Combined with the internal `isAuthenticated && allyApi` guard.
   * Defaults to `true`.
   */
  enabled?: boolean;
  /** Override the global staleTime (ms). Default: 5 minutes. */
  staleTime?: number;
  /** Override the global retry behaviour. Default: false. */
  retry?: boolean | number;
  /**
   * Transform the raw response into the shape callers should receive, e.g.
   * mapping the backend's snake_case payload to a camelCase interface.
   * Omit when the raw response shape is already what callers want.
   */
  select?: (data: TRaw) => TSelected;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Generic partner-API GET hook.
 *
 * Handles Auth0 token acquisition, URL construction, and common query options
 * so individual query hooks only need to declare their endpoint shape.
 *
 * `path` is the single source of truth: it's used both to build the request
 * URL and as the base of the React Query cache key (with `params` appended so
 * different filter combinations get distinct cache entries). This keeps the
 * cache key and the endpoint from drifting apart — callers only state the
 * path once. Import `path` from `./paths` rather than inlining a literal, so
 * mutation hooks can reuse the same constant when invalidating this query.
 *
 * @param path      API path without leading slash, e.g. `"api/v1/partner/users"`.
 * @param params    Optional query-string params; null/undefined values are omitted.
 *                  Also folded into the cache key so distinct param combos are cached separately.
 * @param options   Per-call overrides for enabled, staleTime, retry.
 *
 * @example
 * export const useAllUsersQuery = () =>
 *   useBasicQuery<PartnerUser[]>(PARTNER_USERS_PATH);
 *
 * @example Mapping a snake_case response to a camelCase interface:
 * useBasicQuery<PartnerOrganizationResponse[], PartnerOrganization[]>(PATH, undefined, {
 *   select: (raw) => raw.map(toPartnerOrganization),
 * });
 */
export function useBasicQuery<TRaw, TSelected = TRaw>(
  path: string,
  params?: QueryParams,
  options?: BasicQueryOptions<TRaw, TSelected>
): UseQueryResult<TSelected, Error> {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();

  const enabled = Boolean(isAuthenticated && allyApi) && (options?.enabled ?? true);

  return useQuery<TRaw, Error, TSelected>({
    queryKey: [path, params ?? null],
    enabled,
    staleTime: options?.staleTime ?? QUERY_DEFAULTS.staleTime,
    retry: options?.retry ?? QUERY_DEFAULTS.retry,
    select: options?.select,
    queryFn: async (): Promise<TRaw> => {
      if (!allyApi) throw new Error('Missing allyApi configuration');

      const authHeader = await getAuthHeader(getAccessTokenSilently, auth0Audience);

      return fetchJson<TRaw>(
        joinUrl(allyApi, `${path}${toQueryString(params)}`),
        authHeader,
        `Failed to fetch ${path}`
      );
    },
  });
}
